import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { X, Upload, Image as ImageIcon, Film, Check, Loader2 } from 'lucide-react';
import useInfiniteScroll from '../../hooks/useInfiniteScroll'; // Import generic hook

const MediaManager = ({ isOpen, onClose, onSelect, allowVideo = false }) => {
  const [activeTab, setActiveTab] = useState('library');
  const [mediaFiles, setMediaFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedUrl, setSelectedUrl] = useState(null);
  
  // Pagination State
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  // Reset when opening
  useEffect(() => {
    if (isOpen && activeTab === 'library') {
      setMediaFiles([]);
      setPage(1);
      setHasMore(true);
      fetchMedia(1);
    }
  }, [isOpen, activeTab]);

  const fetchMedia = async (pageNum) => {
    setLoading(true);
    try {
      // Use pagination endpoint
      const res = await axios.get(`${process.env.REACT_APP_API_URL || 'https://konveksiimoonusel.com/api'}/admin/media.php?page=${pageNum}&limit=24`);
      
      // Backend should return { data: [], pagination: { has_more: bool } }
      let newFiles = [];
      let more = false;
      
      if (res.data && Array.isArray(res.data.data)) { // Corrected check
        newFiles = res.data.data;
        more = res.data.pagination ? res.data.pagination.has_more : false;
      } else if (Array.isArray(res.data)) { // Fallback for simple array
        newFiles = res.data;
        more = false; 
      } else {
        console.error("Unexpected media response format:", res.data);
        newFiles = [];
      }

      setMediaFiles(prev => pageNum === 1 ? newFiles : [...prev, ...newFiles]);
      setHasMore(more);
    } catch (err) {
      console.error("Failed to fetch media", err);
    } finally {
      setLoading(false);
    }
  };

  const loadMore = useCallback(() => {
    setPage(prev => {
      const nextPage = prev + 1;
      fetchMedia(nextPage);
      return nextPage;
    });
  }, []);

  // Hook ref
  const lastElementRef = useInfiniteScroll(loadMore, hasMore, loading);

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('image', file);

    setUploading(true);
    try {
      const res = await axios.post(`${process.env.REACT_APP_API_URL || 'https://konveksiimoonusel.com/api'}/admin/upload_image.php`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      // Refresh library
      setPage(1);
      fetchMedia(1);
      setSelectedUrl(res.data.url);
      setActiveTab('library');
    } catch (err) {
      console.error("Upload failed", err);
      alert("Upload failed: " + (err.response?.data?.message || err.message));
    } finally {
      setUploading(false);
    }
  };

  const handleSelect = () => {
    if (selectedUrl) {
      onSelect(selectedUrl);
      onClose();
      setSelectedUrl(null);
    }
  };

  if (!isOpen) return null;

  const styles = {
    overlay: {
      position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)', zIndex: 9999,
      display: 'flex', alignItems: 'center', justifyContent: 'center'
    },
    modal: {
      backgroundColor: '#fff', width: '90%', maxWidth: '800px', height: '80vh',
      borderRadius: '1rem', display: 'flex', flexDirection: 'column', overflow: 'hidden',
      boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
    },
    header: {
      padding: '1rem 1.5rem', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center'
    },
    content: {
      flex: 1, overflowY: 'auto', padding: '1.5rem'
    },
    footer: {
      padding: '1rem 1.5rem', borderTop: '1px solid #e5e7eb', display: 'flex', justifyContent: 'flex-end', gap: '1rem'
    },
    tabBtn: (isActive) => ({
      padding: '0.5rem 1rem', borderRadius: '0.5rem', cursor: 'pointer', fontWeight: '600', fontSize: '0.9rem',
      backgroundColor: isActive ? '#f3f4f6' : 'transparent', color: isActive ? '#111' : '#6b7280', border: 'none', marginRight: '0.5rem'
    }),
    grid: {
      display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '1rem'
    },
    mediaItem: (isSelected) => ({
      position: 'relative', cursor: 'pointer', borderRadius: '0.5rem', overflow: 'hidden',
      border: isSelected ? '3px solid #3b82f6' : '1px solid #e5e7eb',
      aspectRatio: '1 / 1',
      transition: 'all 0.2s'
    }),
    img: { width: '100%', height: '100%', objectFit: 'cover' },
    videoIcon: {
      position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)',
      color: '#fff', backgroundColor: 'rgba(0,0,0,0.5)', borderRadius: '50%', padding: '0.5rem'
    },
    uploadArea: {
      border: '2px dashed #d1d5db', borderRadius: '1rem', height: '100%',
      display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
      color: '#6b7280', cursor: 'pointer', backgroundColor: '#f9fafb'
    }
  };

  // Filter files based on allowVideo
  const filteredFiles = allowVideo 
    ? mediaFiles 
    : mediaFiles.filter(f => f.type === 'image');

  return (
    <div style={styles.overlay} onClick={onClose}>
      <div style={styles.modal} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={styles.header}>
          <div style={{display:'flex', alignItems:'center'}}>
            <h3 style={{margin:0, fontWeight:'700', fontSize:'1.1rem', marginRight:'2rem'}}>Media Manager</h3>
            <div>
              <button style={styles.tabBtn(activeTab === 'library')} onClick={() => setActiveTab('library')}>Library</button>
              <button style={styles.tabBtn(activeTab === 'upload')} onClick={() => setActiveTab('upload')}>Upload New</button>
            </div>
          </div>
          <button onClick={onClose} style={{background:'none', border:'none', cursor:'pointer', color:'#6b7280'}}>
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div style={styles.content}>
          {activeTab === 'library' ? (
            <>
              <div style={styles.grid}>
                {filteredFiles.map((file, idx) => (
                  <div 
                    key={`${file.name}-${idx}`} 
                    style={styles.mediaItem(selectedUrl === file.url)}
                    onClick={() => setSelectedUrl(file.url)}
                    ref={idx === filteredFiles.length - 1 ? lastElementRef : null}
                  >
                    {file.type === 'video' ? (
                      <>
                        <video src={file.url} style={styles.img} />
                        <div style={styles.videoIcon}><Film size={20}/></div>
                      </>
                    ) : (
                      <img src={file.url} alt={file.name} style={styles.img} loading="lazy" />
                    )}
                    {selectedUrl === file.url && (
                      <div style={{position:'absolute', top:'0.25rem', right:'0.25rem', background:'#3b82f6', color:'#fff', borderRadius:'50%', padding:'0.2rem'}}>
                        <Check size={12} />
                      </div>
                    )}
                  </div>
                ))}
                {filteredFiles.length === 0 && !loading && <p>No media found.</p>}
              </div>
              {loading && (
                 <div style={{display:'flex', justifyContent:'center', padding:'1rem', width:'100%', color:'#6b7280'}}>
                   <Loader2 className="animate-spin" /> Loading...
                 </div>
              )}
            </>
          ) : (
            <label style={styles.uploadArea}>
              {uploading ? (
                <>
                  <Loader2 className="animate-spin" size={48} />
                  <p style={{marginTop:'1rem', fontWeight:'500'}}>Uploading...</p>
                </>
              ) : (
                <>
                  <Upload size={48} strokeWidth={1} />
                  <p style={{marginTop:'1rem', fontWeight:'500'}}>Click to Select File</p>
                  <p style={{fontSize:'0.85rem', marginTop:'0.25rem'}}>Images or Videos (MP4, WebM)</p>
                  <input type="file" hidden onChange={handleUpload} accept={allowVideo ? "image/*,video/*" : "image/*"} />
                </>
              )}
            </label>
          )}
        </div>

        {/* Footer */}
        <div style={styles.footer}>
          <button onClick={onClose} style={{padding:'0.75rem 1.5rem', border:'1px solid #d1d5db', background:'#fff', borderRadius:'0.5rem', fontWeight:'600', cursor:'pointer'}}>Cancel</button>
          <button 
            onClick={handleSelect} 
            disabled={!selectedUrl}
            style={{
              padding:'0.75rem 1.5rem', border:'none', background: selectedUrl ? '#18181b' : '#e5e7eb', 
              color: selectedUrl ? '#fff' : '#9ca3af', borderRadius:'0.5rem', fontWeight:'600', 
              cursor: selectedUrl ? 'pointer' : 'not-allowed'
            }}
          >
            Use Selected Media
          </button>
        </div>
      </div>
    </div>
  );
};

export default MediaManager;
